/**
 * 模块页面 —— 展示某模块的二级主题列表。
 */

import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { module, topicsForModule, moduleTint, topics, type ModuleTint } from '@/core/moduleCatalog';
import type { ContentRepository } from '@/core/contentRepository';
import { resolveSymbol } from '@/lib/icons';
import { cn } from '@/lib/cn';
import { entryKindTitle } from '@/core/models';

interface ModulePageProps {
  repository: ContentRepository;
}

const accentColors: Record<ModuleTint, { bg: string; text: string; border: string; light: string }> = {
  ocean: { bg: 'bg-ocean/10', text: 'text-ocean', border: 'border-ocean/30', light: 'bg-ocean/5' },
  teal: { bg: 'bg-teal-500/10', text: 'text-teal-600', border: 'border-teal-500/30', light: 'bg-teal-500/5' },
  indigo: { bg: 'bg-indigo-500/10', text: 'text-indigo-600', border: 'border-indigo-500/30', light: 'bg-indigo-500/5' },
  sage: { bg: 'bg-sage/10', text: 'text-sage', border: 'border-sage/30', light: 'bg-sage/5' },
  plum: { bg: 'bg-plum/10', text: 'text-plum', border: 'border-plum/30', light: 'bg-plum/5' },
  amber: { bg: 'bg-amber/10', text: 'text-amber', border: 'border-amber/30', light: 'bg-amber/5' },
  orange: { bg: 'bg-orange-500/10', text: 'text-orange-600', border: 'border-orange-500/30', light: 'bg-orange-500/5' },
  brown: { bg: 'bg-amber-700/10', text: 'text-amber-800', border: 'border-amber-700/30', light: 'bg-amber-700/5' },
};

export function ModulePage({ repository }: ModulePageProps) {
  const { moduleId } = useParams<{ moduleId: string }>();
  const mod = moduleId ? module(moduleId) : undefined;
  const tint = (mod ? moduleTint[mod.id] : 'sage') as ModuleTint;
  const colors = accentColors[tint] ?? accentColors.sage;

  if (!mod) {
    return (
      <div className="py-20 text-center">
        <p className="text-lg font-semibold text-slate-400">未找到模块</p>
        <Link to="/" className="mt-2 inline-block text-sm text-sage hover:underline">
          返回首页
        </Link>
      </div>
    );
  }

  const topicList = topicsForModule(mod.id);
  const moduleEntries = repository.entries.filter(
    (e) => topics.find((t) => t.id === e.topicID)?.moduleID === mod.id,
  );

  return (
    <div className="space-y-6">
      {/* 头部 */}
      <div className="flex items-center gap-3">
        <Link to="/" className={cn('rounded-lg p-1.5 transition-colors', colors.light, colors.text)}>
          <ArrowLeft size={20} />
        </Link>
        <div>
          <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">{mod.title}</h1>
          <p className="text-xs text-slate-500">{mod.summary} · 共 {moduleEntries.length} 条</p>
        </div>
      </div>

      {/* 主题卡片网格 */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {topicList.map((t) => {
          const Icon = resolveSymbol(t.symbolName);
          const count = repository.entries.filter((e) => e.topicID === t.id).length;
          // 统计该主题下有哪些 kind
          const kinds = new Set(repository.entries.filter((e) => e.topicID === t.id).map((e) => e.kind));
          const kindLabel = [...kinds].map((k) => entryKindTitle[k]).join('、');
          return (
            <Link
              key={t.id}
              to={`/topic/${t.id}`}
              className={cn(
                'group flex items-center gap-3 rounded-2xl border p-4 shadow-sm transition-all',
                'border-slate-200/60 hover:shadow-md dark:border-slate-700/40',
                colors.light,
              )}
            >
              <div className={cn('flex shrink-0 items-center justify-center rounded-xl p-2', colors.bg)}>
                <Icon className={colors.text} size={24} strokeWidth={1.5} />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className={cn('text-sm font-semibold', colors.text)}>{t.title}</h3>
                <p className="mt-0.5 text-xs text-slate-400">
                  {count} 条{kindLabel ? ` · ${kindLabel}` : ''}
                </p>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
